import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, CheckCircle2, Copy, Database, Download, FileJson, KeyRound, Loader2, Plug, ShieldCheck, Sparkles, UploadCloud } from "lucide-react";
import { toast } from "sonner";

const samplePayload = {
  name: "Jane Doe",
  url: "https://www.linkedin.com/in/example",
  title: "Senior Director, Commercial Strategy",
  org: "Example Biotherapeutics",
  summary: "Commercial leader with product launch experience.",
  education: [{ degree: "Master of Business Administration", school_name: "Example University" }],
  experience: [{ title: "Senior Director", company: "Example Biotherapeutics", is_current: true, company_domain: "examplebio.com", yearly_revenue_range: "75M-200M" }],
  structured_location: { country: "United States", state: "California" },
};

export default function CandidateUpload() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [payload, setPayload] = useState("");
  const [sourceSystem, setSourceSystem] = useState("linkedin_profile_payload");
  const [validating, setValidating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [validation, setValidation] = useState(null);
  const [result, setResult] = useState(null);
  const [enriching, setEnriching] = useState(false);
  const [enrichment, setEnrichment] = useState(null);
  const [enrichmentConfig, setEnrichmentConfig] = useState(null);
  const [clayConfig, setClayConfig] = useState(null);

  useEffect(() => {
    api.get("/admin/candidate-enrichment/config")
      .then(res => setEnrichmentConfig(res.data))
      .catch(() => setEnrichmentConfig(null));
    api.get("/admin/clay-integration/config")
      .then(res => setClayConfig(res.data))
      .catch(() => setClayConfig(null));
  }, [api]);

  const copyText = async (value, message) => {
    if (!value) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        throw new Error("Clipboard unavailable");
      }
      toast.success(message);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = value;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const copied = document.execCommand("copy");
      document.body.removeChild(textarea);
      copied ? toast.success(message) : toast.error("Copy failed — select and copy manually");
    }
  };

  const readFile = async (file) => {
    if (!file) return;
    const text = await file.text();
    setPayload(text);
    setValidation(null);
    setResult(null);
    toast.success(`Loaded ${file.name}`);
  };

  const downloadTemplate = () => {
    const blob = new Blob([JSON.stringify([samplePayload], null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "varyynt-candidate-upload-template.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const validateUpload = async () => {
    if (!payload.trim()) { toast.error("Add a JSON payload first"); return; }
    setValidating(true);
    setResult(null);
    setEnrichment(null);
    try {
      const res = await api.post("/admin/candidate-uploads/validate", { payload, source_system: sourceSystem });
      setValidation(res.data);
      toast.success("Payload validated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Validation failed");
    } finally {
      setValidating(false);
    }
  };

  const importUpload = async () => {
    if (!validation) { toast.error("Validate the payload before importing"); return; }
    setImporting(true);
    try {
      const res = await api.post("/admin/candidate-uploads/import", { payload, source_system: sourceSystem });
      setResult(res.data);
      setEnrichment(null);
      toast.success("Candidate import finished");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const enrichBatch = async () => {
    if (!result?.batch_id) { toast.error("Import a batch before enrichment"); return; }
    setEnriching(true);
    try {
      const res = await api.post(`/admin/candidate-uploads/${result.batch_id}/enrich`, { limit: 25, force: false });
      setEnrichment(res.data);
      if (res.data.failed > 0) {
        toast.error("AI enrichment finished with errors");
      } else if (res.data.redaction_blocked > 0) {
        toast.warning("Some summaries were blocked by redaction checks");
      } else {
        toast.success("AI enrichment complete");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI enrichment failed");
    } finally {
      setEnriching(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="candidate-upload-page">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-8 animate-fadeIn">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-lime-50 text-lime-700 border border-lime-200" data-testid="phase-one-badge">Phase 1 + 2</Badge>
            <Badge variant="outline" className="font-mono text-xs" data-testid="admin-only-badge">Admin only</Badge>
          </div>
          <h1 className="text-2xl font-heading text-slate-900">Candidate Upload</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">Ingest raw JSON candidate profiles, preserve the original payload, enrich profiles with AI, and publish only redaction-safe client summaries.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="candidate-upload-template-button" variant="outline" onClick={downloadTemplate}>
            <Download className="w-4 h-4 mr-2" /> Template
          </Button>
          <Button data-testid="candidate-upload-review-button" variant="outline" onClick={() => navigate("/admin/candidate-review")}>Review Queue</Button>
          <Button data-testid="candidate-upload-back-button" variant="ghost" onClick={() => navigate("/admin/candidates")}>Back to Database</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_420px] gap-6">
        <div className="space-y-6">
          <Card className="rounded-xl border bg-white shadow-sm" data-testid="candidate-upload-input-card">
            <CardContent className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-[220px_minmax(0,1fr)] gap-4">
                <div>
                  <label htmlFor="source-system" className="text-xs font-mono uppercase tracking-widest text-slate-400">Source system</label>
                  <Input id="source-system" data-testid="candidate-upload-source-input" value={sourceSystem} onChange={e => setSourceSystem(e.target.value)} className="mt-2" />
                </div>
                <div>
                  <label htmlFor="candidate-file" className="text-xs font-mono uppercase tracking-widest text-slate-400">Upload JSON / NDJSON</label>
                  <div className="mt-2 flex items-center gap-3">
                    <Input id="candidate-file" data-testid="candidate-upload-file-input" type="file" accept=".json,.ndjson,application/json" onChange={e => readFile(e.target.files?.[0])} />
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="candidate-payload" className="text-xs font-mono uppercase tracking-widest text-slate-400">Raw payload</label>
                <Textarea
                  id="candidate-payload"
                  data-testid="candidate-upload-payload-textarea"
                  value={payload}
                  onChange={e => { setPayload(e.target.value); setValidation(null); setResult(null); }}
                  placeholder="Paste a single candidate JSON object, a JSON array, or NDJSON records..."
                  className="mt-2 min-h-[360px] font-mono text-xs leading-relaxed"
                />
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-slate-100 pt-5">
                <div className="flex items-center gap-2 text-xs text-slate-500" data-testid="candidate-upload-phase-note">
                  <FileJson className="w-4 h-4 text-slate-400" /> Import first, then run AI enrichment with redaction checks.
                </div>
                <div className="flex gap-2">
                  <Button data-testid="candidate-upload-validate-button" variant="outline" onClick={validateUpload} disabled={validating || importing}>
                    {validating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Validate
                  </Button>
                  <Button data-testid="candidate-upload-import-button" onClick={importUpload} disabled={!validation || importing || validating} className="bg-evergreen-950 hover:bg-evergreen-900 text-white">
                    {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UploadCloud className="w-4 h-4 mr-2" />} Import
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {validation?.preview?.length > 0 && (
            <Card className="rounded-xl border bg-white shadow-sm" data-testid="candidate-upload-preview-card">
              <CardContent className="p-0 overflow-x-auto">
                <div className="min-w-[900px] text-sm">
                  <div className="grid grid-cols-[90px_220px_1.2fr_180px_180px_220px] bg-slate-50">
                    {['Action', 'Candidate', 'Title', 'Function', 'TA', 'Status'].map(h => <div key={h} className="px-4 py-3 text-xs font-mono uppercase tracking-widest text-slate-400">{h}</div>)}
                  </div>
                  {validation.preview.map(item => (
                    <div key={`${item.row}-${item.anonymous_id}`} className="grid grid-cols-[90px_220px_1.2fr_180px_180px_220px] border-t border-slate-100" data-testid={`candidate-upload-preview-row-${item.row}`}>
                      <div className="px-4 py-3"><Badge variant="outline" className="font-mono text-xs">{item.action}</Badge></div>
                      <div className="px-4 py-3"><div className="font-medium text-slate-900">{item.name || "Unnamed"}</div><div className="text-xs font-mono text-slate-400">{item.anonymous_id}</div></div>
                      <div className="px-4 py-3 text-slate-600 truncate">{item.current_title || "—"}</div>
                      <div className="px-4 py-3 text-slate-600">{item.functional_area || "—"}</div>
                      <div className="px-4 py-3 text-slate-600 text-xs">{(item.therapeutic_areas || []).join(", ") || "—"}</div>
                      <div className="px-4 py-3">
                        <Badge className={item.processing_status === "processed" ? "bg-lime-50 text-lime-700 border border-lime-200" : "bg-amber-50 text-amber-700 border border-amber-200"}>
                          {item.processing_status.replace("_", " ")}
                        </Badge>
                        {item.warnings?.length > 0 && <p className="text-xs text-amber-700 mt-1 max-w-xs">{item.warnings.join("; ")}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <aside className="space-y-4">
          <Card className="rounded-xl border bg-white shadow-sm" data-testid="candidate-upload-summary-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <Database className="w-4 h-4 text-evergreen-950" />
                <h2 className="font-heading text-base text-slate-900">Validation summary</h2>
              </div>
              {validation ? (
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(validation.counts || {}).map(([key, value]) => (
                    <div key={key} className="rounded-lg bg-slate-50 border border-slate-100 p-3" data-testid={`candidate-upload-count-${key}`}>
                      <p className="text-xs font-mono uppercase tracking-widest text-slate-400">{key.replace("_", " ")}</p>
                      <p className="text-2xl font-accent font-bold text-slate-900">{value}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">Validate a payload to see create/update counts and records that need review.</p>
              )}
            </CardContent>
          </Card>

          {result && (
            <Card className="rounded-xl border bg-white shadow-sm" data-testid="candidate-upload-result-card">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center gap-2">
                  {result.failed > 0 ? <AlertTriangle className="w-4 h-4 text-amber-600" /> : <CheckCircle2 className="w-4 h-4 text-lime-700" />}
                  <h2 className="font-heading text-base text-slate-900">Import result</h2>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {["created", "updated", "needs_review", "failed"].map(key => (
                    <div key={key} className="rounded-lg bg-slate-50 border border-slate-100 p-3" data-testid={`candidate-upload-result-${key}`}>
                      <p className="text-xs font-mono uppercase tracking-widest text-slate-400">{key.replace("_", " ")}</p>
                      <p className="text-2xl font-accent font-bold text-slate-900">{result[key]}</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs font-mono text-slate-400 break-all" data-testid="candidate-upload-batch-id">Batch {result.batch_id}</p>
                <Button data-testid="candidate-upload-enrich-button" onClick={enrichBatch} disabled={enriching || result.failed === result.total} className="w-full bg-lime-600 hover:bg-lime-700 text-white">
                  {enriching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  {enriching ? "Enriching..." : "Run AI Enrichment"}
                </Button>
              </CardContent>
            </Card>
          )}

          <Card className="rounded-xl border bg-white shadow-sm" data-testid="candidate-enrichment-config-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck className="w-4 h-4 text-evergreen-950" />
                <h2 className="font-heading text-base text-slate-900">AI enrichment</h2>
              </div>
              <div className="space-y-2 text-sm text-slate-600">
                <p data-testid="candidate-enrichment-model">Model: <span className="font-mono text-slate-900">{enrichmentConfig?.model || "—"}</span></p>
                <p data-testid="candidate-enrichment-provider">Provider: <span className="font-mono text-slate-900">{enrichmentConfig?.provider || "—"}</span></p>
                <p className="text-xs text-slate-400">Client-facing summaries are sanitized and blocked if PII, LinkedIn URLs, domains, or employer names remain.</p>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-xl border bg-white shadow-sm" data-testid="clay-integration-config-card">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center gap-2">
                <Plug className="w-4 h-4 text-evergreen-950" />
                <h2 className="font-heading text-base text-slate-900">Clay API Push</h2>
              </div>
              <p className="text-xs text-slate-500">Use this in Clay HTTP API enrichment to send rows into Varyynt. Varyynt never pulls from Clay.</p>
              <div className="space-y-3">
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-slate-400">Endpoint</p>
                  <div className="mt-1 flex gap-2">
                    <Input data-testid="clay-integration-endpoint-input" readOnly value={clayConfig?.endpoint_url || ""} className="font-mono text-xs" />
                    <Button data-testid="clay-integration-copy-endpoint-button" variant="outline" size="icon" onClick={() => copyText(clayConfig?.endpoint_url, "Clay endpoint copied")}><Copy className="w-4 h-4" /></Button>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest text-slate-400">Authorization Header</p>
                  <div className="mt-1 flex gap-2">
                    <Input data-testid="clay-integration-auth-input" readOnly value={clayConfig?.authorization_header || ""} className="font-mono text-xs" type="password" />
                    <Button data-testid="clay-integration-copy-auth-button" variant="outline" size="icon" onClick={() => copyText(clayConfig?.authorization_header, "Clay authorization header copied")}><KeyRound className="w-4 h-4" /></Button>
                  </div>
                </div>
                <Button data-testid="clay-integration-copy-body-button" variant="outline" className="w-full" onClick={() => copyText(JSON.stringify(clayConfig?.body_template || {}, null, 2), "Clay body template copied")}>
                  <Copy className="w-4 h-4 mr-2" /> Copy Body Template
                </Button>
                <p className="text-xs text-slate-400" data-testid="clay-integration-rate-limit">Rate limit: {clayConfig?.rate_limit_per_minute || "—"} rows/min</p>
              </div>
            </CardContent>
          </Card>

          {enrichment && (
            <Card className="rounded-xl border bg-white shadow-sm" data-testid="candidate-enrichment-result-card">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-lime-700" />
                  <h2 className="font-heading text-base text-slate-900">Enrichment result</h2>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {["processed", "redaction_blocked", "failed", "total"].map(key => (
                    <div key={key} className="rounded-lg bg-slate-50 border border-slate-100 p-3" data-testid={`candidate-enrichment-result-${key}`}>
                      <p className="text-xs font-mono uppercase tracking-widest text-slate-400">{key.replace("_", " ")}</p>
                      <p className="text-2xl font-accent font-bold text-slate-900">{enrichment[key]}</p>
                    </div>
                  ))}
                </div>
                {enrichment.errors?.length > 0 && <p className="text-xs text-red-600" data-testid="candidate-enrichment-errors">{enrichment.errors[0].error}</p>}
                {(enrichment.redaction_blocked > 0 || enrichment.failed > 0) && (
                  <Button data-testid="candidate-enrichment-review-queue-button" variant="outline" onClick={() => navigate("/admin/candidate-review")} className="w-full">Open Review Queue</Button>
                )}
              </CardContent>
            </Card>
          )}
        </aside>
      </div>
    </div>
  );
}